import { toastError, toastSuccess } from '@app/components/Toastify';
import { showToastWithAction } from '@app/components/ToastWithAction';
import { useGetReportQueries } from '@app/hooks/queries/useGetReportQueries';
import { Report } from '@app/hooks/queries/useGetReportQueries/types';
import { handleStoreConnectionError } from '@app/pages/advanced/components/EmptyState/helpers';
import { Store } from '@app/types/store';
import { openModal } from '@app/utils/modals';
import { pushState } from '@app/utils/routing';
import angular from 'angular';
import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const UploadErrorModal = lazy(() => import('@/features/Stores/components/UploadErrorModal'));

export const useReports = (storeByReportId: { [reportId: string]: Store }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const AppCuesService: any = useMemo(
    () => angular.element(document.body).injector().get('AppCuesService'),
    []
  );
  const { formatMessage } = useIntl();
  const [reportIds, setReportIds] = useState<string[]>([]);
  const queries = useGetReportQueries(reportIds);
  const [reportCompleteStatus, setReportCompleteStatus] = useState<{ [reportId: string]: boolean }>(
    {}
  );

  // isComplete indicates if all reports have been processed either "complete" or "failed"
  const [isComplete, setIsComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // hasOneStoreOrdersSynced indicates if at least one store has orders synced
  const [hasOneStoreOrdersSynced, setHasOneStoreOrdersSynced] = useState(false);

  const handleNoOrdersFound = useCallback(
    (report: Report) => {
      const id = 'reports.store-no-paid-orders';
      const store = storeByReportId[report.id];
      let errorMessage = report.message?.body;

      if (store && !errorMessage) {
        errorMessage = formatMessage({ id }, { store: store.name }).replace(
          '<a>',
          `<a href="/store/${store.id}">`
        );
      }

      if (errorMessage) {
        showToastWithAction(errorMessage, {
          actionIcon: 'close',
          onActionIconClick: () => undefined,
          toastId: `${id}-${report.id}`,
        });
      }
    },
    [formatMessage, storeByReportId]
  );

  const processReport = useCallback(
    (report: Report) => {
      // Check if there were any orders to download
      if ((report.total === 0 && report.estimatedRows === null) || report.estimatedRows === 0) {
        handleNoOrdersFound(report);
        return;
      }

      if (report.errors) {
        // There were errors
        if (report.errors === 'Incorrect Format') {
          const id = 'reports.incorrect-format';
          toastError(formatMessage({ id }), {
            toastId: id,
          });
          return;
        }

        if (report.errors === 'No Shipments') {
          const id = 'reports.no-shipments';
          toastError(formatMessage({ id }), {
            toastId: id,
          });
          return;
        }
      }

      // There were no errors

      if (report.failure > 0) {
        openModal(UploadErrorModal, { report });
      }

      if (report.success > 0) {
        const id = 'reports.orders-success';
        toastSuccess(
          formatMessage({ id }, { count: report.success, store: storeByReportId[report.id].name }),
          {
            toastId: `${id}-${report.id}`,
          }
        );

        AppCuesService.track('Advanced | Created Shipment', null, true);

        if (!hasOneStoreOrdersSynced) {
          setHasOneStoreOrdersSynced(true);
        }
      }
    },
    [AppCuesService, formatMessage, handleNoOrdersFound, hasOneStoreOrdersSynced, storeByReportId]
  );

  useEffect(() => {
    const ids = Object.keys(storeByReportId);
    setReportIds(ids);
    setReportCompleteStatus(
      ids.reduce((acc, id) => {
        acc[id] = false;
        return acc;
      }, {} as typeof reportCompleteStatus)
    );

    if (ids.length) {
      setIsProcessing(true);
    }
  }, [storeByReportId]);

  useEffect(() => {
    queries.forEach((query) => {
      if (query.isSuccess) {
        const report = query.data as Report;
        const { id: reportId, state: reportState, message } = report;

        if (reportCompleteStatus[reportId]) {
          // This report is already complete
          return;
        }

        if (reportState === 'failed') {
          if (/connection\serror/.test(message?.body ?? '')) {
            const store = storeByReportId[reportId];
            handleStoreConnectionError(
              store,
              formatMessage({ id: 'reports.store-connection-error' }, { store: store.name }),
              formatMessage({ id: 'global.fix-now' })
            );
          } else {
            const id = 'toast.default-error';
            toastError(message?.body || formatMessage({ id }), {
              toastId: id,
            });
          }

          setReportCompleteStatus({ ...reportCompleteStatus, [reportId]: true });
          return;
        }

        if (reportState === 'complete') {
          processReport(report);
          setReportCompleteStatus({ ...reportCompleteStatus, [reportId]: true });
        }
      } else if (query.isError) {
        const id = 'toast.default-error';
        toastError(formatMessage({ id }), { toastId: id });
      }
    });
  }, [formatMessage, processReport, queries, reportCompleteStatus, storeByReportId]);

  useEffect(() => {
    const isComplete = Object.values(reportCompleteStatus).every(Boolean);
    if (isComplete) {
      setIsComplete(true);
      setIsProcessing(false);

      if (hasOneStoreOrdersSynced) {
        pushState('/advanced?page=1&runWalkthrough=order-sync-with-missing-dimensions', true);
      }
    }
  }, [hasOneStoreOrdersSynced, reportCompleteStatus]);

  return { isComplete, isProcessing };
};
